/**
 * CopilotLauncher — floating SAP-Joule-style AI assistant UI.
 * Bottom-right bubble opens a chat panel available on every page. All state
 * lives in CopilotContext (mounted globally in App.js) so the conversation
 * survives page navigations.
 */
import React, { useRef, useEffect, useState } from 'react';
import { Sparkles, X, Send, Loader2, ArrowUpRight, Check, Ban } from 'lucide-react';
import { useCopilot } from '../../context/CopilotContext';
import CopilotChart from './CopilotChart';

const SUGGESTIONS = [
  'Top 5 customers by revenue',
  'Take me to create a sales order',
  'Break down sales orders by status',
  'How many open purchase orders?',
];

const CopilotLauncher = () => {
  const ctx = useCopilot();
  const [input, setInput] = useState('');
  const scrollRef = useRef(null);

  const { open, setOpen, messages, busy, send, resolvePending, navigate } = ctx || {};

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy, open]);

  if (!ctx) return null;

  const submit = (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput('');
    send(msg);
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="copilot-launcher"
          className="fixed bottom-6 right-6 z-[60] flex items-center gap-2 rounded-full bg-primary px-4 py-3 text-white shadow-lg shadow-primary/30 transition-transform hover:scale-105 active:scale-95"
          aria-label="Open AI Copilot"
        >
          <Sparkles className="h-5 w-5" />
          <span className="text-sm font-semibold">Ask Copilot</span>
        </button>
      )}

      {open && (
        <div
          data-testid="copilot-panel"
          className="fixed bottom-6 right-6 z-[60] flex h-[600px] max-h-[85vh] w-[400px] max-w-[calc(100vw-3rem)] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
        >
          <div className="flex items-center justify-between bg-primary px-4 py-3 text-white">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              <div>
                <p className="text-sm font-semibold leading-tight">ERP Copilot</p>
                <p className="text-[11px] text-white/70 leading-tight">Navigate · Ask · Act</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="copilot-close" aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-gray-50 p-4">
            {messages.length === 0 && (
              <div className="mt-2" data-testid="copilot-empty">
                <p className="text-sm text-gray-600">
                  Hi! I can help you <b>navigate</b>, answer questions from your live
                  data, and perform actions (with your confirmation). Try:
                </p>
                <div className="mt-3 flex flex-col gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => submit(s)}
                      data-testid="copilot-suggestion"
                      className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-xs text-gray-700 transition-colors hover:border-primary hover:text-primary"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m) => (
              <div key={m.id} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                <div
                  data-testid={`copilot-msg-${m.role}`}
                  className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm ${
                    m.role === 'user'
                      ? 'rounded-br-sm bg-primary text-white'
                      : 'rounded-bl-sm border border-gray-200 bg-white text-gray-800'
                  }`}
                >
                  {m.text}
                  {m.streaming && (
                    <span className="ml-0.5 inline-block h-3.5 w-1.5 translate-y-0.5 animate-pulse bg-primary/70" data-testid="copilot-stream-cursor" />
                  )}

                  {(m.charts || []).map((c, i) => (
                    <CopilotChart key={i} spec={c} />
                  ))}

                  {(m.actions || []).filter((a) => a.type === 'navigate').map((a, i) => (
                    <button
                      key={i}
                      onClick={() => navigate(a.path)}
                      data-testid="copilot-nav-action"
                      className="mt-2 flex items-center gap-1 rounded-lg bg-primary/10 px-2.5 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/20"
                    >
                      <ArrowUpRight className="h-3.5 w-3.5" /> Open {a.label}
                    </button>
                  ))}

                  {(m.pending || []).map((p) => (
                    <div key={p.pending_id} className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5" data-testid="copilot-confirm-card">
                      <p className="text-xs font-medium text-amber-900">{p.summary}</p>
                      {!m.done ? (
                        <div className="mt-2 flex gap-2">
                          <button
                            onClick={() => resolvePending(m.id, p, true)}
                            disabled={busy}
                            data-testid="copilot-confirm-yes"
                            className="flex items-center gap-1 rounded-md bg-green-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                          >
                            <Check className="h-3.5 w-3.5" /> Confirm
                          </button>
                          <button
                            onClick={() => resolvePending(m.id, p, false)}
                            disabled={busy}
                            data-testid="copilot-confirm-no"
                            className="flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                          >
                            <Ban className="h-3.5 w-3.5" /> Cancel
                          </button>
                        </div>
                      ) : (
                        <p className="mt-1 text-[11px] italic text-gray-500">Resolved</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {busy && !messages.some((x) => x.streaming && x.text) && (
              <div className="flex justify-start" data-testid="copilot-typing">
                <div className="flex items-center gap-2 rounded-2xl rounded-bl-sm border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" /> Thinking…
                </div>
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); submit(); }}
            className="flex items-center gap-2 border-t border-gray-200 bg-white p-3"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask or tell me what to do…"
              data-testid="copilot-input"
              className="flex-1 rounded-full border border-gray-200 px-4 py-2 text-sm outline-none focus:border-primary"
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              data-testid="copilot-send"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white transition-transform hover:scale-105 disabled:opacity-40"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default CopilotLauncher;
